import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { local } from 'embroider-css-modules';
import { COLOR_PARITY_LOSS } from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import gaugeStyles from 'lumen/components/parity/charts/gauge.module.css';
import changeColorClass from 'lumen/helpers/change-color-class';
import type { ParityStrategyEvolution } from 'lumen/models';
import { PARITY_FILTER } from 'lumen/utils/constants/pi-constant-values';

import styles from './chart-parity-strategy-gauge.module.css';

const SCORE_INNER_RADIUS = '85%';
const SCORE_OUTER_RADIUS = '95%';

type CustomPointOptionsObject = Highcharts.PointOptionsObject & {
  formattedValue: string;
};

type GaugeSeries = Omit<Highcharts.SeriesSolidgaugeOptions, 'data'> & {
  data: CustomPointOptionsObject[];
};

interface ParityChartsChartParityStrategyGaugeSignature {
  Args: {
    // dateAggregation: string; // PS-TODO #DATE_AGG
    isBenchmarkSelected: boolean;
    isLoading: boolean;
    previousPeriodData: ParityStrategyEvolution;
    selectedPeriodData: ParityStrategyEvolution;
  };
}

export default class ParityChartsChartParityStrategyGauge extends Component<ParityChartsChartParityStrategyGaugeSignature> {
  @service declare intl: Services['intl'];
  @service declare psSelections: Services['ps-selections'];

  get changeVersusLastPeriod(): number {
    const difference =
      this.args.selectedPeriodData.lossScore -
      this.args.previousPeriodData.lossScore;
    return parseFloat(difference.toFixed(2));
  }

  get chartData(): GaugeSeries[] {
    const parityData = [];
    parityData.push({
      id: PARITY_FILTER.LOSS,
      name: this.intl.t('parity_analytics.loss'),
      formattedValue: this.args.selectedPeriodData.formattedLossScore,
      color: `var(${COLOR_PARITY_LOSS})`,
      innerRadius: SCORE_INNER_RADIUS,
      radius: SCORE_OUTER_RADIUS,
      y: this.args.selectedPeriodData.lossScore * 100,
    });

    return [{ type: 'solidgauge', data: parityData }];
  }

  get chartOptions(): Highcharts.Options {
    return {
      chart: {
        type: 'solidgauge',
        height: '50%',
      },

      tooltip: {
        backgroundColor: undefined,
        borderWidth: 0,
        className: gaugeStyles['tooltip-widget'],
        shadow: false,
        useHTML: true,
        pointFormatter(this: Highcharts.Point): string {
          const options = this.options as CustomPointOptionsObject;
          return `${this.name}: ${options.formattedValue}`;
        },
      },

      pane: {
        center: ['50%', '100%'],
        size: '180%',
        startAngle: -90,
        endAngle: 90,

        background: [
          {
            shape: 'arc',
            innerRadius: SCORE_INNER_RADIUS,
            outerRadius: SCORE_OUTER_RADIUS,
            backgroundColor: 'var(--prism-color-chart-neutral-figure-empty)',
            borderWidth: 0,
          },
        ],
      },

      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
        className: gaugeStyles['gauge-label'],
        title: {
          align: 'low',
          textAlign: 'center',
          text: this.args.selectedPeriodData.formattedLossScore,
          style: { color: 'var(--prism-color-text-neutral-default)' },
        },
      },

      plotOptions: {
        solidgauge: {
          dataLabels: { enabled: false },
          rounded: true,
          tooltip: { followPointer: true },
        },
      },
    };
  }

  get formattedChangeVersusLastPeriod(): string {
    if (this.args.previousPeriodData.isMissing) {
      return this.intl.t('general.missing');
    }

    // PS-TODO #DATE_AGG
    // const versusString = this.intl.t(`ranking.vs_last_${this.args.dateAggregation}`);
    const versusString = this.intl.t('ranking.vs_last_week');
    if (this.changeVersusLastPeriod === 0) {
      return `${this.intl.t('ranking.no_change')} ${versusString}`;
    }

    return `${this.intl.formatNumber(this.changeVersusLastPeriod, {
      style: 'percent',
      signDisplay: 'exceptZero',
    })} ${versusString}`;
  }

  get labelForRecords(): string {
    return this.psSelections.isHotelSelected
      ? this.intl.t('parity_analytics.number_of_shops')
      : this.intl.t('general.hotels');
  }

  <template>
    <PrismCard @isSecondary={{true}}>
      <:content>
        <div class="m-100 mt-400">
          <div class={{styles.gauge-chart}}>
            {{#unless @isLoading}}
              <GeneralChartsOtaChart
                @chartOptions={{this.chartOptions}}
                @model={{this.chartData}}
              />
            {{/unless}}
          </div>

          <div class={{local styles "widget-row" "widget-row--top"}}>
            {{#if @isLoading}}
              <div class={{styles.widget-row--loading}}><span
                  class="loading-pulse w-1/2"
                /></div>
            {{else}}
              <h4
                class="truncate
                  {{changeColorClass
                    this.changeVersusLastPeriod
                    invertColors=true
                  }}"
              >
                {{this.formattedChangeVersusLastPeriod}}
              </h4>
            {{/if}}
          </div>

          <div class={{local styles "widget-row" "widget-row--large"}}>
            <div class="w-1/2 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3 class="truncate">
                  {{@selectedPeriodData.numberOfRecords}}
                </h3>
              {{/if}}
              <p class="text-200--regular truncate">{{this.labelForRecords}}</p>
            </div>
            <div class="w-1/2 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3
                  class="truncate"
                >{{@selectedPeriodData.numberOfRecordsWithIssues}}</h3>
              {{/if}}
              <p class="text-200--regular truncate">
                {{t "parity_strategy.with_issues"}}
              </p>
            </div>
          </div>
        </div>
      </:content>
    </PrismCard>
  </template>
}
